import { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import { useApi } from '@/providers/ApiContext';
import { deleteChartWaypoint } from '@/actions/chartWaypoint';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import type { ChartWaypoint, EntityId } from '@/types';

interface ChartWaypointProps {
  waypoint: ChartWaypoint;
  chartId: EntityId;
  onEdit?: (waypoint: ChartWaypoint) => void;
  onDelete?: () => void;
  isEditable?: boolean;
}

function ChartWaypointComponent({
  waypoint,
  chartId,
  onEdit,
  onDelete,
  isEditable = true,
}: ChartWaypointProps): React.JSX.Element {
  const api = useApi();
  const [api_error, setApiError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleDelete = async (): Promise<void> => {
    if (
      !window.confirm(
        `Are you sure you want to delete waypoint "${waypoint.name}"?`,
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      if (!api) return;
      await deleteChartWaypoint(api, chartId, waypoint.entity_id);
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting waypoint:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to delete the waypoint. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
            {waypoint.name}
          </Typography>
        </Box>
        {isEditable && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onEdit && (
              <IconButton
                size='small'
                onClick={() => onEdit(waypoint)}
                color='primary'
                sx={{ p: 0.5 }}
                disabled={deleting}
              >
                <EditOutlinedIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size='small'
                onClick={handleDelete}
                color='error'
                sx={{ p: 0.5 }}
                disabled={deleting}
              >
                <DeleteOutlinedIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default ChartWaypointComponent;

